/**
 * Initializes native mobile features when running in Capacitor
 * - Push notifications
 * - Keyboard handling
 * - Swipe-back navigation
 *
 * NOTE: Deep link handling is now in DeepLinkHandler.tsx, which is rendered
 * at the app root (outside auth gate) to handle links when logged out.
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { StatusBar, Style } from '@capacitor/status-bar';
import { isNative, initKeyboardListeners } from '@/lib/capacitor';
import { usePushNotifications } from '@/layout/usePushNotifications';

// Parse a CSS color (hex or rgb()) → { hex: '#rrggbb', lum: 0..1 } so the native
// status bar can be tinted to match the active theme's surface color.
function parseColor(input: string): { hex: string; lum: number } {
  const c = input.trim();
  let r = 255,
    g = 255,
    b = 255;
  if (c.startsWith('#')) {
    const h = c.slice(1);
    const hh = h.length === 3 ? h.split('').map((x) => x + x).join('') : h.slice(0, 6);
    r = parseInt(hh.slice(0, 2), 16);
    g = parseInt(hh.slice(2, 4), 16);
    b = parseInt(hh.slice(4, 6), 16);
  } else {
    const m = c.match(/\d+(\.\d+)?/g);
    if (m && m.length >= 3) [r, g, b] = [Number(m[0]), Number(m[1]), Number(m[2])];
  }
  const hx = (n: number) => Math.round(n).toString(16).padStart(2, '0');
  return { hex: `#${hx(r)}${hx(g)}${hx(b)}`, lum: (0.299 * r + 0.587 * g + 0.114 * b) / 255 };
}

export function NativeInitializer() {
  const navigate = useNavigate();
  const location = useLocation();

  usePushNotifications();

  useEffect(() => {
    if (!isNative()) return;
    initKeyboardListeners();
  }, []);

  // Native status bar: stop it overlaying the WebView (otherwise the app header
  // renders under the status bar / Dynamic Island and its controls can't be
  // tapped), then tint the now-visible status-bar strip to the active theme's
  // surface color so it sits flush with the header instead of defaulting to
  // black. Re-applied on theme change. Style picks dark/light text by luminance.
  useEffect(() => {
    if (!isNative()) return;
    StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    const apply = () => {
      const surface = getComputedStyle(document.body).getPropertyValue('--color-surface');
      const { hex, lum } = parseColor(surface || '#ffffff');
      StatusBar.setBackgroundColor({ color: hex }).catch(() => {});
      StatusBar.setStyle({ style: lum > 0.55 ? Style.Light : Style.Dark }).catch(() => {});
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  // Swipe-back navigation
  useEffect(() => {
    if (!isNative()) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartTime = Date.now();
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = Math.abs(touch.clientY - touchStartY);
      const deltaTime = Date.now() - touchStartTime;

      // Swipe from left edge (within 30px), right direction, more horizontal than vertical
      // Fast swipe (under 300ms) or longer swipe (over 100px)
      const isFromLeftEdge = touchStartX < 30;
      const isRightSwipe = deltaX > 50;
      const isHorizontal = deltaX > deltaY * 1.5;
      const isFastSwipe = deltaTime < 300 && deltaX > 50;
      const isLongSwipe = deltaX > 100;

      if (isFromLeftEdge && isRightSwipe && isHorizontal && (isFastSwipe || isLongSwipe)) {
        // Don't go back from root routes
        const rootRoutes = ['/login'];
        const isAtRoot = rootRoutes.some(route =>
          location.pathname === route
        ) || (
          location.pathname.startsWith('/w/') &&
          location.pathname.includes('/c/') &&
          !location.pathname.includes('/chat')
        );

        if (!isAtRoot && window.history.length > 1) {
          navigate(-1);
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [navigate, location.pathname]);

  return null;
}
